import React from 'react';
import { LayoutGrid, List } from 'lucide-react';

// Apple-inspired color palette consistent with the app (enhanced)
const colors = {
  accent: '#1e40af',
  accentHover: '#1e3a8a',
  accentLight: 'rgba(30, 64, 175, 0.1)',
  white: '#ffffff',
  gray50: '#f9f9f9',
  gray100: '#f3f3f3',
  gray200: '#e6e6e6',
  gray300: '#d1d1d1',
  gray500: '#8e8e8e',
  gray700: '#636363',
  gray900: '#333333',
};

// Global font styles
const fontStyles = {
  fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
  medium: {
    fontWeight: 500,
  },
};

const ViewToggle = ({ viewMode, onViewModeChange }) => {
  const handleViewChange = (newViewMode) => {
    onViewModeChange(newViewMode);
    // Persist user preference
    localStorage.setItem('signpact-view-mode', newViewMode);
  };

  return (
    <div 
      className="relative flex items-center p-1 border rounded-xl transition-all duration-300"
      style={{
        background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(249, 249, 249, 0.8))',
        borderColor: colors.gray200,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06)',
        backdropFilter: 'blur(8px)',
        fontFamily: fontStyles.fontFamily,
        height: '42px'
      }}
    >
      {/* Sliding Background Indicator */}
      <div
        className="absolute top-1 bottom-1 rounded-[10px] transition-all duration-300 ease-out"
        style={{
          left: viewMode === 'grid' ? '4px' : 'calc(50% - 2px)',
          width: 'calc(50% - 6px)',
          background: `linear-gradient(135deg, ${colors.accent}, ${colors.accentHover})`,
          boxShadow: `0 2px 8px rgba(30, 64, 175, 0.2)`,
          zIndex: 1
        }}
      />
      
      <div className="relative flex z-10 w-full">
        <button
          className="flex-1 px-3 py-2 text-sm font-medium transition-all duration-300 ease-out flex items-center justify-center rounded-[10px] group"
          onClick={() => handleViewChange('grid')}
          style={{
            color: viewMode === 'grid' ? colors.white : colors.gray700,
            letterSpacing: '-0.01em',
            ...fontStyles.medium,
            textShadow: viewMode === 'grid' ? '0 1px 2px rgba(0, 0, 0, 0.1)' : 'none'
          }}
          onMouseEnter={(e) => {
            if (viewMode !== 'grid') {
              e.target.style.color = colors.gray900;
              e.target.style.transform = 'scale(1.02)';
            } else {
              e.target.style.transform = 'scale(1.01)';
            }
          }}
          onMouseLeave={(e) => {
            if (viewMode !== 'grid') {
              e.target.style.color = colors.gray700;
              e.target.style.transform = 'scale(1)';
            } else {
              e.target.style.transform = 'scale(1)';
            }
          }}
          title="Grid view"
          aria-label="Switch to grid view"
        >
          <LayoutGrid 
            size={16} 
            className="mr-1.5 transition-all duration-300 group-hover:scale-110" 
            style={{
              filter: viewMode === 'grid' ? 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))' : 'none'
            }}
          />
          <span className="transition-all duration-300">Grid</span>
        </button>
        
        <button
          className="flex-1 px-3 py-2 text-sm font-medium transition-all duration-300 ease-out flex items-center justify-center rounded-[10px] group"
          onClick={() => handleViewChange('list')}
          style={{
            color: viewMode === 'list' ? colors.white : colors.gray700,
            letterSpacing: '-0.01em',
            ...fontStyles.medium,
            textShadow: viewMode === 'list' ? '0 1px 2px rgba(0, 0, 0, 0.1)' : 'none'
          }}
          onMouseEnter={(e) => {
            if (viewMode !== 'list') {
              e.target.style.color = colors.gray900;
              e.target.style.transform = 'scale(1.02)';
            } else {
              e.target.style.transform = 'scale(1.01)';
            }
          }}
          onMouseLeave={(e) => {
            if (viewMode !== 'list') {
              e.target.style.color = colors.gray700;
              e.target.style.transform = 'scale(1)';
            } else {
              e.target.style.transform = 'scale(1)';
            }
          }}
          title="List view"
          aria-label="Switch to list view"
        >
          <List 
            size={16} 
            className="mr-1.5 transition-all duration-300 group-hover:scale-110" 
            style={{
              filter: viewMode === 'list' ? 'drop-shadow(0 1px 2px rgba(0, 0, 0, 0.1))' : 'none'
            }}
          />
          <span className="transition-all duration-300">List</span>
        </button>
      </div>
    </div>
  );
};

export default ViewToggle; 